import {
  Redirect,
  Route,
  Switch,
  useRouteMatch,
  useLocation,
} from 'react-router-dom';
import { ProfileSettingsPage } from './ProfileSettingsPage';
import { Box, HStack, Stack, Button } from '@chakra-ui/react';
import { PageHeading } from '../../shared/Typography';
import {
  NavList,
  NavLinkOption,
  SideNavigationWithContent,
} from '../../shared/SideNavigationWithContent';
import { ProfileIcon } from '../../shared/icons/Profile';
import { SecurityIcon } from '../../shared/icons/Security';
import { TeamIcon } from '../../shared/icons/Team';
import { SecuritySettingsPage } from './SecuritySettingsPage';
import { StarCircleIcon } from '../../shared/icons/StarCircle';
import { PageContainer } from '../../components/PageContainer';
import { InviteMemberButton } from '../components/InviteMemberButton';
import { MembersTable } from '../components/MembersTable';
import { usePermissions } from '../../hooks/use-permissions';
import { PERMISSION } from '../../constant/roles-and-permissions';
import { BrandSettingsPage } from './BrandSettingsPage';
import React from 'react';
import { useBrandSettingsState } from './useBrandSettingsState';
import { COLORS } from '../../constant';

export const SettingsRouter = () => {
  const location = useLocation();
  const navigationOptions: (NavLinkOption | undefined)[] = [
    {
      label: 'Branding',
      href: '/app/settings/branding',
      icon: StarCircleIcon,
    },
    {
      label: 'Team',
      href: '/app/settings/team',
      icon: TeamIcon,
    },
    {
      label: 'Profile',
      href: '/app/settings/profile',
      icon: ProfileIcon,
    },
    {
      label: 'Security',
      href: '/app/settings/security',
      icon: SecurityIcon,
    },
  ].filter((e) => e !== undefined);

  const { path } = useRouteMatch();
  const roleCanInviteMember = usePermissions([PERMISSION.Members.Create]);

  // Branding state/handlers (only initialize if on branding route)
  const isBrandingRoute = location.pathname.endsWith('/branding');
  const brandSettings = useBrandSettingsState();

  return (
    <Box>
      <PageContainer>
        <Stack spacing={6} maxW={{ base: 'full', lg: '85%' }}>
          <HStack minH="40px" justify="space-between">
            <PageHeading>Settings</PageHeading>
            {/* Render Save/Cancel buttons if on branding route */}
            {isBrandingRoute && (
              <HStack spacing={2}>
                <Button
                  onClick={brandSettings.handleCancel}
                  width={'fit-content'}
                  variant="secondary"
                  size="md"
                  isDisabled={!brandSettings.hasChanges()}
                >
                  Cancel
                </Button>
                <Button
                  onClick={brandSettings.handleSave}
                  width={'fit-content'}
                  variant="primaryPurple"
                  _hover={{ bg: COLORS.FINCH.BLACK }}
                  isLoading={brandSettings.updateBranding.isPending}
                  isDisabled={
                    brandSettings.updateBranding.isPending ||
                    !brandSettings.hasChanges() ||
                    (brandSettings.isEditingName &&
                      brandSettings.editedName.length > 50)
                  }
                >
                  Save
                </Button>
              </HStack>
            )}
            {/* Invite Member button for Team page */}
            {roleCanInviteMember && (
              <Route path={`${path}/team`}>
                <InviteMemberButton />
              </Route>
            )}
          </HStack>
          <SideNavigationWithContent
            nav={<NavList options={navigationOptions as NavLinkOption[]} />}
          >
            <Switch>
              <Route path={`${path}/branding`}>
                <BrandSettingsPage {...brandSettings} COLORS={COLORS} />
              </Route>
              <Route path={`${path}/profile`}>
                <ProfileSettingsPage />
              </Route>
              <Route path={`${path}/team`}>
                <MembersTable />
              </Route>
              <Route path={`${path}/security`}>
                <SecuritySettingsPage />
              </Route>
              <Route path="*">
                <Redirect to={`${path}/branding`} />
              </Route>
            </Switch>
          </SideNavigationWithContent>
        </Stack>
      </PageContainer>
    </Box>
  );
};
